import React, { useState, useEffect } from 'react';
import { Table, Button, Space, message, Tag, Select, DatePicker, Card, Statistic, Row, Col } from 'antd';
import { CheckOutlined, DollarOutlined } from '@ant-design/icons';
import api from '../services/api';
import dayjs from 'dayjs';

const { Option } = Select;
const { MonthPicker } = DatePicker;

const Commissions = () => {
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [filters, setFilters] = useState({
    status: '',
    affiliate_id: ''
  });
  const [affiliates, setAffiliates] = useState([]);
  const [report, setReport] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(dayjs());

  const fetchCommissions = async (page = 1) => {
    setLoading(true);
    try {
      const response = await api.get('/commissions', {
        params: {
          page,
          limit: pagination.pageSize,
          ...filters
        }
      });
      setCommissions(response.data.data);
      setPagination(prev => ({
        ...prev,
        current: page,
        total: response.data.total
      }));
    } catch (error) {
      message.error('Erro ao carregar comissões');
    } finally {
      setLoading(false);
    }
  };

  const fetchAffiliates = async () => {
    try {
      const response = await api.get('/affiliates', { params: { limit: 100 } });
      setAffiliates(response.data.data);
    } catch (error) {
      console.error('Erro ao carregar afiliados');
    }
  };

  const fetchReport = async () => {
    try {
      const response = await api.get('/commissions/report', {
        params: {
          month: selectedMonth.month() + 1,
          year: selectedMonth.year()
        }
      });
      setReport(response.data);
    } catch (error) {
      message.error('Erro ao carregar relatório');
    }
  };

  useEffect(() => {
    fetchCommissions();
    fetchAffiliates();
    fetchReport();
  }, []);

  useEffect(() => {
    fetchCommissions(1);
  }, [filters]);

  useEffect(() => {
    fetchReport();
  }, [selectedMonth]);

  const handleTableChange = (pagination) => {
    fetchCommissions(pagination.current);
  };

  const handleMarkAsPaid = async (id) => {
    try {
      await api.put(`/commissions/${id}/pay`);
      message.success('Comissão marcada como paga');
      fetchCommissions(pagination.current);
      fetchReport();
    } catch (error) {
      message.error('Erro ao marcar comissão como paga');
    }
  };

  const columns = [
    {
      title: 'Afiliado',
      dataIndex: 'affiliate_name',
      key: 'affiliate_name',
    },
    {
      title: 'Código',
      dataIndex: 'affiliate_code',
      key: 'affiliate_code',
      render: (code) => <Tag color="blue">{code}</Tag>
    },
    {
      title: 'Time',
      dataIndex: 'customer_name',
      key: 'customer_name',
    },
    {
      title: 'Telefone',
      dataIndex: 'cellphone_number',
      key: 'cellphone_number',
    },
    {
      title: 'Valor Original',
      dataIndex: 'original_amount',
      key: 'original_amount',
      render: (amount) => `R$ ${parseFloat(amount).toFixed(2)}`
    },
    {
      title: 'Taxa (%)',
      dataIndex: 'commission_rate',
      key: 'commission_rate',
      render: (rate) => `${rate}%`
    },
    {
      title: 'Comissão',
      dataIndex: 'commission_amount',
      key: 'commission_amount',
      render: (amount) => `R$ ${parseFloat(amount).toFixed(2)}`
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'PAID' ? 'green' : status === 'PENDING' ? 'orange' : 'red'}>
          {status === 'PAID' ? 'PAGO' : status === 'PENDING' ? 'PENDENTE' : 'CANCELADO'}
        </Tag>
      )
    },
    {
      title: 'Data Pagamento',
      dataIndex: 'payment_date',
      key: 'payment_date',
      render: (date) => date ? dayjs(date).format('DD/MM/YYYY') : '-'
    },
    {
      title: 'Criado em',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => dayjs(date).format('DD/MM/YYYY HH:mm')
    },
    {
      title: 'Ações',
      key: 'actions',
      render: (_, record) => (
        <Space>
          {record.status === 'PENDING' && (
            <Button
              type="primary"
              icon={<CheckOutlined />}
              size="small"
              onClick={() => handleMarkAsPaid(record.id)}
            >
              Marcar como Pago
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const reportColumns = [
    {
      title: 'Afiliado',
      dataIndex: 'affiliate_name',
      key: 'affiliate_name',
    },
    {
      title: 'Código',
      dataIndex: 'affiliate_code',
      key: 'affiliate_code',
      render: (code) => <Tag color="blue">{code}</Tag>
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Total Comissões',
      dataIndex: 'total_commissions',
      key: 'total_commissions',
    },
    {
      title: 'Valor Total',
      dataIndex: 'total_amount',
      key: 'total_amount',
      render: (amount) => `R$ ${parseFloat(amount || 0).toFixed(2)}`
    },
    {
      title: 'Pago',
      dataIndex: 'paid_amount',
      key: 'paid_amount',
      render: (amount) => `R$ ${parseFloat(amount || 0).toFixed(2)}`
    },
    {
      title: 'Pendente',
      dataIndex: 'pending_amount',
      key: 'pending_amount',
      render: (amount) => `R$ ${parseFloat(amount || 0).toFixed(2)}`
    },
  ];

  const totalPending = report.reduce((sum, item) => sum + parseFloat(item.pending_amount || 0), 0);
  const totalPaid = report.reduce((sum, item) => sum + parseFloat(item.paid_amount || 0), 0);

  return (
    <div>
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={8}>
          <Card style={{minHeight: "100px", display: "flex", alignItems: "end"}}>
            <Statistic
              title="Total Pendente"
              value={totalPending}
              precision={2}
              prefix="R$"
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card style={{minHeight: "100px", display: "flex", alignItems: "end"}}>
            <Statistic
              style={{height: "100%"}}
              title="Total Pago"
              value={totalPaid}
              precision={2}
              prefix="R$"
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card style={{minHeight: "100px", display: "flex", alignItems: "end"}}>
            <Statistic
              title="Total Geral"
              value={totalPending + totalPaid}
              precision={2}
              prefix="R$"
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
      </Row>

      <Card title="Relatório Mensal" style={{ marginBottom: 24 }}>
        <div style={{ marginBottom: 16 }}>
          <MonthPicker
            value={selectedMonth}
            onChange={setSelectedMonth}
            format="MM/YYYY"
            placeholder="Selecione o mês"
          />
        </div>
        <Table
          columns={reportColumns}
          dataSource={report}
          rowKey="affiliate_id"
          pagination={false}
          size="small"
        />
      </Card>

      <Card title="Todas as Comissões">
        <div style={{ marginBottom: 16, display: 'flex', gap: 16 }}>
          <Select
            placeholder="Filtrar por status"
            allowClear
            style={{ width: 200 }}
            value={filters.status}
            onChange={(value) => setFilters(prev => ({ ...prev, status: value || '' }))}
          >
            <Option value="PENDING">Pendente</Option>
            <Option value="PAID">Pago</Option>
            <Option value="CANCELLED">Cancelado</Option>
          </Select>

          <Select
            placeholder="Filtrar por afiliado"
            allowClear
            style={{ width: 200 }}
            value={filters.affiliate_id}
            onChange={(value) => setFilters(prev => ({ ...prev, affiliate_id: value || '' }))}
          >
            {affiliates.map(affiliate => (
              <Option key={affiliate.id} value={affiliate.id}>
                {affiliate.name} ({affiliate.affiliate_code})
              </Option>
            ))}
          </Select>
        </div>

        <Table
          columns={columns}
          dataSource={commissions}
          rowKey="id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
        />
      </Card>
    </div>
  );
};

export default Commissions;